import { useEffect, useCallback } from 'react';

export interface UseKeyboardNavOptions {
  onNext: () => void;
  onPrev: () => void;
  onHome: () => void;
  onEnd: () => void;
  onToggleToc: () => void;
  onToggleBookmarks: () => void;
  onToggleBacklog: () => void;
  onToggleSettings: () => void;
  onCloseAll: () => void;
  isAnyDrawerOpen: boolean;
  enabled?: boolean;
}

export function useKeyboardNav({
  onNext,
  onPrev,
  onHome,
  onEnd,
  onToggleToc,
  onToggleBookmarks,
  onToggleBacklog,
  onToggleSettings,
  onCloseAll,
  isAnyDrawerOpen,
  enabled = true,
}: UseKeyboardNavOptions) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      // Ignore keyboard shortcuts if the user is typing in an input or textarea
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        if (e.key === 'Escape') {
          onCloseAll();
          target.blur();
        }
        return;
      }

      // If a drawer is open, Escape should close it
      if (e.key === 'Escape') {
        if (isAnyDrawerOpen) {
          e.preventDefault();
          onCloseAll();
        }
        return;
      }

      // Do not navigate pages if an overlay drawer or modal is open
      if (isAnyDrawerOpen) {
        return;
      }

      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
          e.preventDefault();
          onNext();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          onPrev();
          break;
        case ' ':
          e.preventDefault();
          if (e.shiftKey) {
            onPrev();
          } else {
            onNext();
          }
          break;
        case 'Home':
          e.preventDefault();
          onHome();
          break;
        case 'End':
          e.preventDefault();
          onEnd();
          break;
        case 't':
        case 'T':
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            onToggleToc();
          }
          break;
        case 'b':
        case 'B':
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            onToggleBookmarks();
          }
          break;
        case 'l':
        case 'L':
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            onToggleBacklog();
          }
          break;
        case 's':
        case 'S':
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            onToggleSettings();
          }
          break;
      }
    },
    [
      enabled,
      isAnyDrawerOpen,
      onNext,
      onPrev,
      onHome,
      onEnd,
      onToggleToc,
      onToggleBookmarks,
      onToggleBacklog,
      onToggleSettings,
      onCloseAll,
    ]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
